import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function Select({ options, value, onChange, placeholder, className }: SelectProps): JSX.Element {
  // radix Select 不接受空字符串 value/Item value——「全部」类选项约定 value=''，内部映射为占位符
  const EMPTY = '__empty__';
  const toInternal = (v: string) => (v === '' ? EMPTY : v);
  const selected = options.find((o) => o.value === value);
  return (
    <SelectPrimitive.Root value={toInternal(value)} onValueChange={(v) => onChange(v === EMPTY ? '' : v)}>
      <SelectPrimitive.Trigger
        className={cn(
          'flex items-center justify-between rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground',
          'hover:bg-muted/80 focus:outline-none focus:border-primary',
          'data-[placeholder]:text-muted-foreground',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder}>
          {selected?.label ?? placeholder ?? '选择...'}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon className="ml-2 text-muted-foreground">
          <ChevronDown size={14} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="z-[100] overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
          position="popper"
          sideOffset={4}
        >
          <SelectPrimitive.Viewport>
            {options.map((opt) => (
              <SelectPrimitive.Item
                key={opt.value}
                value={toInternal(opt.value)}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm text-foreground outline-none',
                  'hover:bg-primary/10 focus:bg-primary/10',
                  'data-[state=checked]:bg-primary/15 data-[state=checked]:text-primary',
                )}
              >
                <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
